import { z } from "zod";

/**
 * Startseite "Aufgaben" und Bereichsansicht: die API liefert Rohdaten, das Web
 * baut jeden Satz per `t()` aus `quelle` + `ziel`.
 */

export const aufgabeQuelleSchema = z.enum([
  "todo",
  "besuch",
  "systemstand",
  "statusbericht",
  "freigabe",
  "datenluecke",
]);
export type AufgabeQuelle = z.infer<typeof aufgabeQuelleSchema>;

export const aufgabeDringlichkeitSchema = z.enum(["ueberfaellig", "woche", "spaeter"]);
export type AufgabeDringlichkeit = z.infer<typeof aufgabeDringlichkeitSchema>;

/** Wohin ein Klick auf die Aufgabe führt. */
export const aufgabeZielSchema = z.discriminatedUnion("art", [
  z.object({
    art: z.literal("weekly"),
    leadEmployeeId: z.string().uuid(),
    segmentId: z.string().uuid(),
  }),
  z.object({ art: z.literal("kunde"), customerId: z.string().uuid() }),
  z.object({ art: z.literal("besuche"), customerId: z.string().uuid() }),
  z.object({ art: z.literal("systemstand"), customerId: z.string().uuid() }),
  z.object({
    art: z.literal("statusbericht"),
    engagementId: z.string().uuid(),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  }),
  z.object({ art: z.literal("freigabe_besuch") }),
  z.object({ art: z.literal("freigabe_kampagne") }),
  z.object({ art: z.literal("datenluecken") }),
]);
export type AufgabeZiel = z.infer<typeof aufgabeZielSchema>;

/**
 * `id` = `"<quelle>:<fachliche id>"`, stabil über Abrufe. `titel` nur bei Todos
 * (Todo-Text) und Kampagnen-Freigaben (Kampagnentitel). `herkunft` = Datum des
 * Weeklys (Todo), des letzten Besuchs bzw. der letzten Bestätigung (Systemstand).
 */
export const aufgabeSchema = z.object({
  id: z.string(),
  quelle: aufgabeQuelleSchema,
  dringlichkeit: aufgabeDringlichkeitSchema,
  titel: z.string().nullable(),
  kundeName: z.string().nullable(),
  herkunft: z.string().date().nullable(),
  seit: z.string().date().nullable(),
  faelligAm: z.string().date().nullable(),
  abhakbar: z.boolean(),
  todoId: z.string().uuid().nullable(),
  ziel: aufgabeZielSchema,
  antragsteller: z.string().nullable(),
});
export type Aufgabe = z.infer<typeof aufgabeSchema>;

export const aufgabenResponseSchema = z.object({
  items: z.array(aufgabeSchema),
  nichtZuordenbar: z.object({
    anzahl: z.number().int(),
    beispiele: z.array(z.string()).max(3),
  }),
});
export type AufgabenResponse = z.infer<typeof aufgabenResponseSchema>;

export const bereichLeadSchema = z.object({
  employeeId: z.string().uuid(),
  name: z.string(),
  todosUeberfaellig: z.number().int(),
  besucheFaellig: z.number().int(),
  systemstandUeberfaellig: z.number().int(),
  statusberichteOffen: z.number().int(),
  items: z.array(aufgabeSchema),
});
export type BereichLead = z.infer<typeof bereichLeadSchema>;

export const bereichResponseSchema = z.object({
  leads: z.array(bereichLeadSchema),
  freigaben: z.array(aufgabeSchema),
});
export type BereichResponse = z.infer<typeof bereichResponseSchema>;

/** Ein Punkt der Datenlücken-Übersicht (`GET /dashboard/hinweise`). */
export const dashboardHinweisSchema = z.object({
  anzahl: z.number().int(),
  beispiele: z.array(z.object({ text: z.string(), customerId: z.string().nullable() })),
});
export type DashboardHinweis = z.infer<typeof dashboardHinweisSchema>;

export const dashboardHinweiseSchema = z.object({
  projekteOhneBeauftragung: dashboardHinweisSchema,
  beauftragungenOhneLead: dashboardHinweisSchema,
  beauftragungenOhneZiel: dashboardHinweisSchema,
  statusberichteEntwurf: dashboardHinweisSchema,
  kundenabteilungenUnverknuepft: dashboardHinweisSchema,
  segmentIds: z.array(z.string()),
  alleSegmente: z.boolean(),
});
export type DashboardHinweise = z.infer<typeof dashboardHinweiseSchema>;
